import { readFileSync } from "node:fs";
import { join } from "node:path";
import type { ConnectorManager } from "@skaile/workspaces/connectors";
import { createLogger } from "@skaile/workspaces/core/logging";
import type { Logger } from "@skaile/workspaces/types";
import { PluginStore } from "./store.js";

/**
 * Construction options for {@link WorkspacePlugin}.
 *
 * At minimum `projectDir` must be supplied.  All other fields are optional
 * and are pre-resolved by the caller when available (e.g. the runner already
 * knows `driver`/`provider`/`model` from its own settings resolution).
 *
 * @docLink packages/workspace-plugin/concepts#workspaceplugin-lifecycle
 */
export interface WorkspacePluginOpts {
  projectDir: string;
  /**
   * Active connector manager. The unified `ConnectorManager` owns both
   * filesystem-face (mountable) and tool-face connectors.
   */
  connectorManager?: ConnectorManager;
  bootOwned?: boolean;
  onLog?: (msg: string) => void;
  /** Pre-resolved driver type (avoids needing boot() to read settings). */
  driver?: string;
  /** Pre-resolved provider. */
  provider?: string;
  /** Pre-resolved model. */
  model?: string;
}

/**
 * Shared runtime core for the skaile workspace plugin.
 *
 * Manages the full lifecycle (`boot` → tools available → `shutdown`) and
 * surfaces workspace capabilities — mounts, connectors, flows, skills,
 * sessions, and config validation — through two adapter backends:
 *
 * - **MCP, in-process** (runner): call {@link buildWorkspaceMcpServer} for the
 *   Claude Agent SDK server the runner embeds into its `query()` call.
 * - **MCP, stdio** (`skaile mcp-server`): call {@link runMcpServerStdio} for a
 *   raw `@modelcontextprotocol/sdk` server — no Claude Agent SDK.
 * - **OMP**: call {@link buildOmpToolSet} for the 13-tool native subset and
 *   {@link buildOmpExtensionSource} for the generated extension file.
 *
 * Two boot modes are supported via {@link WorkspacePluginOpts.bootOwned}:
 * - `bootOwned: false` (default) — managers injected externally by the runner.
 * - `bootOwned: true` — plugin owns the full manager lifecycle (standalone MCP
 *   subprocess or OMP extension).
 *
 * @docLink packages/workspace-plugin/concepts#workspaceplugin-lifecycle
 */
export class WorkspacePlugin {
  readonly projectDir: string;
  connectorManager: ConnectorManager | null;
  readonly store: PluginStore;

  private readonly bootOwned: boolean;
  private readonly onLog: (msg: string) => void;
  private readonly log: Logger = createLogger({
    kind: "mcp",
    subkind: "skaile-workspace",
  });
  driver?: string;
  provider?: string;
  model?: string;

  constructor(opts: WorkspacePluginOpts) {
    this.projectDir = opts.projectDir;
    this.connectorManager = opts.connectorManager ?? null;
    this.store = new PluginStore(join(opts.projectDir, ".skaile", "plugin-state.json"));
    this.bootOwned = opts.bootOwned ?? false;
    if (opts.driver) this.driver = opts.driver;
    if (opts.provider) this.provider = opts.provider;
    if (opts.model) this.model = opts.model;
    this.onLog = opts.onLog ?? (() => {});
    this.log.debug("plugin constructed", {
      driver: opts.driver,
      hasConnectors: !!opts.connectorManager,
      provider: opts.provider,
      model: opts.model,
      bootOwned: this.bootOwned,
    });
  }

  async boot(): Promise<void> {
    if (!this.bootOwned) {
      // Managers were injected externally — resolve settings only (non-fatal)
      try {
        const { resolveSettings } = await import("@skaile/workspaces/core");
        const settings = await resolveSettings(this.projectDir);
        this.driver = settings.driver ?? "omp";
        this.provider = settings.provider;
        this.model = settings.model;
      } catch {
        /* non-fatal */
      }
      await this.store.load();
      return;
    }

    // bootOwned: we own the managers lifecycle
    try {
      const { resolveSettings } = await import("@skaile/workspaces/core");
      const settings = await resolveSettings(this.projectDir);
      this.driver = settings.driver ?? "omp";
      this.provider = settings.provider;
      this.model = settings.model;
    } catch {
      /* non-fatal */
    }

    try {
      const {
        ConnectorManager,
        loadConnectorDeclarations,
        registerBuiltinConnectors,
        createCliSecretProviderChain,
      } = await import("@skaile/workspaces/connectors");

      registerBuiltinConnectors();

      const secrets = createCliSecretProviderChain();

      const connectorDecls = await loadConnectorDeclarations(this.projectDir);
      const cm = new ConnectorManager(this.projectDir, { secrets });
      await cm.connectAll(connectorDecls);
      this.connectorManager = cm;

      this.log.info("workspace plugin booted", {
        connectors: connectorDecls.length,
      });
      this.onLog(`WorkspacePlugin booted: ${connectorDecls.length} connectors`);
    } catch (err) {
      this.log.warn("workspace plugin boot warning", {
        error: err instanceof Error ? err.message : String(err),
      });
      this.onLog(`WorkspacePlugin boot warning: ${err}`);
    }

    await this.store.load();
  }

  async reload(): Promise<void> {
    if (!this.bootOwned) return;
    await this.shutdown();
    await this.boot();
  }

  async shutdown(): Promise<void> {
    this.log.info("workspace plugin shutdown");
    await this.store.flush();
    if (!this.bootOwned) return;
    try {
      await (this.connectorManager as any)?.disconnectAll?.();
    } catch {
      /* best-effort */
    }
    this.connectorManager = null;
  }

  buildSystemPromptSection(driver?: string): string {
    const d = driver ?? this.driver ?? "unknown";
    // The unified ConnectorManager owns both mountable (filesystem-face) and
    // tool-face connectors. Filesystem-face connectors expose a `mountPath`.
    const all = (this.connectorManager as any)?.listConnectors?.() ?? [];
    const mounts = (all as any[]).filter((c) => typeof c.mountPath === "string");
    const connectors = (all as any[]).filter((c) => typeof c.mountPath !== "string");
    const lines = ["## Workspace (skaile)", "", `Driver: ${d}`, `Project: ${this.projectDir}`];
    if (mounts.length > 0) {
      lines.push("", "Mounts:");
      for (const v of mounts) {
        lines.push(`  · ${v.id} (${v.driver}) → ${v.mountPath ?? "(unmounted)"}`);
      }
    }
    if (connectors.length > 0) {
      lines.push("", "Connectors:");
      for (const c of connectors) {
        lines.push(`  · ${c.id} (${c.driver})`);
      }
    }
    lines.push("", "Use workspace_info for full details. Use workspace_validate to check health.");

    for (const name of ["SOUL.md", "RULES.md"]) {
      const skaileOverride = join(this.projectDir, ".skaile", name);
      const rootFile = join(this.projectDir, name);
      for (const candidate of [skaileOverride, rootFile]) {
        try {
          const content = readFileSync(candidate, "utf-8").trim();
          if (content) {
            lines.push("", `## ${name.replace(".md", "")}`, "", content);
            break;
          }
        } catch {
          // file not found, try next
        }
      }
    }

    return lines.join("\n");
  }

  /**
   * Build the in-process Claude Agent SDK MCP server (runner embedding).
   * Returns `null` when `@anthropic-ai/claude-agent-sdk` is unavailable.
   */
  async buildWorkspaceMcpServer(): Promise<unknown | null> {
    this.log.info("building workspace MCP server");
    try {
      const { buildWorkspaceMcpServer } = await import("./adapters/mcp.js");
      const server = await buildWorkspaceMcpServer(this);
      if (server) this.log.info("workspace MCP server ready");
      else this.log.debug("workspace MCP server skipped (no tools)");
      return server;
    } catch (err) {
      this.log.error("workspace MCP server build failed", err);
      throw err;
    }
  }

  /**
   * Build the raw `@modelcontextprotocol/sdk` stdio MCP server — no Claude
   * Agent SDK. Used by the `skaile mcp-server` subprocess. The returned
   * server is not yet connected to a transport.
   */
  async buildWorkspaceMcpStdioServer(): Promise<unknown> {
    this.log.info("building workspace stdio MCP server");
    const { buildWorkspaceMcpStdioServer } = await import("./adapters/mcp.js");
    return buildWorkspaceMcpStdioServer(this);
  }

  /**
   * Build and run the stdio MCP server: builds the raw
   * `@modelcontextprotocol/sdk` server and connects a `StdioServerTransport`.
   * Used by the `skaile mcp-server` subprocess.
   */
  async runMcpServerStdio(): Promise<void> {
    this.log.info("starting workspace stdio MCP server");
    const { runMcpServerStdio } = await import("./adapters/mcp.js");
    await runMcpServerStdio(this);
  }

  async buildOmpToolSet(): Promise<unknown[]> {
    const { buildOmpToolSet } = await import("./adapters/omp.js");
    return buildOmpToolSet(this);
  }

  async buildOmpExtensionSource(): Promise<string> {
    const { buildOmpExtensionSource } = await import("./adapters/omp.js");
    return buildOmpExtensionSource(this.projectDir);
  }

  async handleSlashCommand(group: string, args: string[]): Promise<string> {
    const { handleSlashCommand } = await import("./adapters/omp.js");
    return handleSlashCommand(this, group, args);
  }
}
